// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import {
  Component,
  DestroyRef,
  Inject,
  type OnDestroy,
  type OnInit,
  PLATFORM_ID,
  SecurityContext,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { DomSanitizer, Title } from '@angular/platform-browser';
import { environment } from '../../environments/environment';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { UtilService } from '../shared/services/util.service';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { CmsService } from '../shared/services/cms.service';
import { isPlatformBrowser } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { StorageService } from '../shared/services/storage.service';
import { StorageEnum } from '../shared/enums/storage.enum';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { SeoService } from '../shared/services/seo.service';

@Component({
  selector: 'app-advantage',
  imports: [MatButtonModule],
  templateUrl: './advantage.component.html',
  styleUrl: './advantage.component.scss',
})
export class AdvantageComponent implements OnInit, OnDestroy {
  env = environment;
  isMobile = false;
  isBrowser = false;
  pageReady = false;
  pageLabels: any = {};
  perks: any[] = [];
  isLoggedIn = false;
  sanitizedHeaderText: string = '';
  sanitizedCtaHeaderText: string = '';
  sanitizedAuthCtaHeaderText: string = '';
  sanitizedDisclaimerText: string = '';
  sanitizedSubHeaderText: string = '';
  iframeLoaded = false;
  private readonly iframeSpinnerDelayMs = 1200;
  private resizeListener?: () => void;

  constructor(
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) readonly platformId: string,
    private readonly utils: UtilService,
    private readonly cms: CmsService,
    private readonly storage: StorageService,
    private readonly sanitizer: DomSanitizer,
    private readonly titleService: Title,
    private readonly destroyRef: DestroyRef,
    private readonly seoS: SeoService,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    this.cms
      .getContent('oas-advantage', 'OAS Advantage')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.pageLabels = JSON.parse(JSON.stringify(data));

        // Sanitize HTML content to prevent XSS
        this.sanitizedHeaderText = this.sanitizer.sanitize(SecurityContext.HTML, data.advantageHeaderText) ?? '';
        this.sanitizedCtaHeaderText =
          this.sanitizer.sanitize(SecurityContext.HTML, data.unauthenticatedCtaHeaderText) ?? '';
        this.sanitizedAuthCtaHeaderText =
          this.sanitizer.sanitize(SecurityContext.HTML, data.authenticatedCtaHeaderText) ?? '';
        this.sanitizedDisclaimerText = this.sanitizer.sanitize(SecurityContext.HTML, data.disclaimerText) ?? '';
        this.sanitizedSubHeaderText = this.sanitizer.sanitize(SecurityContext.HTML, data.advantageSubheaderText) ?? '';

        this.getPerks(this.pageLabels);
        if (this.isBrowser) {
          this.isMobile = this.utils.isMobile(992);
          if (typeof window !== 'undefined') {
            this.resizeListener = () => {
              this.isMobile = this.utils.isMobile(992);
            };
            window.addEventListener('resize', this.resizeListener);
          }
          if (this.utils.isNullOrUndefined(this.storage.getSession(StorageEnum.LoginResponse))) {
            this.isLoggedIn = false;
          } else {
            this.isLoggedIn = true;
          }
        }
        if (!this.utils.isNullOrUndefined(data) && !this.isLoggedIn) {
          this.seoS.setSeoTags(data.seo);
        }
        this.setPageTitle();
        this.pageReady = true;
      });
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined' && this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  getPerks(data: any): void {
    const items = data.perks[0];
    if (!items) return;
    Object.values(items).forEach((item: any) => {
      this.perks.push(item.value?.data);
    });
  }

  // Set the page title based on the user's login status
  private setPageTitle(): void {
    const loggedOutTitle = this.pageLabels.unauthenticatedTitleSeo;
    const loggedInTitle = this.pageLabels.authenticatedTitleSeo;
    this.titleService.setTitle(this.isLoggedIn ? loggedInTitle : loggedOutTitle);
  }

  onIframeLoad(): void {
    if (this.iframeLoaded) return;
    this.iframeLoaded = true;
    setTimeout(() => {
      this.utils.hideSpinner();
    }, this.iframeSpinnerDelayMs);
  }

  onIframeError(): void {
    if (this.iframeLoaded) return;
    this.iframeLoaded = true;
    setTimeout(() => {
      this.utils.hideSpinner();
    }, this.iframeSpinnerDelayMs);
  }
}
